import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, 
  User, 
  LogOut, 
  Home, 
  FileText, 
  ClipboardList, 
  HelpCircle,
  Settings,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import PortalNavigation from './PortalNavigation';
import PortalFooter from './PortalFooter';

/**
 * PortalLayout - Consistent layout wrapper for all policyholder portal pages
 * 
 * Provides:
 * - Sticky header with logo, page title, notifications, profile
 * - Mobile hamburger menu navigation
 * - Consistent footer
 * - Scroll to top on navigation
 */
export default function PortalLayout({ 
  children, 
  pageTitle = 'Dashboard',
  policyNumber,
  userName,
  claimsCount = 0,
  showBackButton = false,
  onBack
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);

  // Get policy number from props or user context
  const effectivePolicyNumber = policyNumber || user?.policyNumber || user?.policies?.[0]?.policy_number;

  // Navigation items for desktop top nav
  const navItems = [
    { path: '/portal', label: 'Dashboard', icon: Home, exact: true },
    { path: effectivePolicyNumber ? `/documents/${effectivePolicyNumber}` : '/portal', label: 'Documents', icon: FileText, pathMatch: '/documents' },
    { path: '/claims', label: 'Claims', icon: ClipboardList, badge: claimsCount },
    { path: '/help', label: 'Help', icon: HelpCircle },
  ];

  // Check if nav item is active
  const isNavActive = (item) => {
    const pathToCheck = item.pathMatch || item.path;
    if (item.exact) {
      return location.pathname === pathToCheck;
    }
    return location.pathname.startsWith(pathToCheck);
  };

  // Create portal container on mount
  useEffect(() => {
    // Create or find the portal container
    let container = document.getElementById('mobile-nav-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'mobile-nav-portal';
      document.body.appendChild(container);
    }
    setPortalContainer(container);
    
    return () => {
      // Clean up only if we created it and menu is closed
      if (container && !mobileMenuOpen) {
        // Don't remove, just leave it for reuse
      }
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const handleLogoClick = () => {
    navigate('/portal');
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Sticky Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo */}
            <div className="flex items-center">
              <button 
                onClick={handleLogoClick}
                className="flex-shrink-0 focus:outline-none"
              >
                <img 
                  src="https://images.losspay.com/losspay/losspay-logo.png" 
                  alt="LossPay" 
                  className="h-7 sm:h-8"
                />
              </button>
            </div>

            {/* Center: Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isNavActive(item);
                
                return (
                  <button
                    key={item.label}
                    onClick={() => {
                      navigate(item.path);
                      window.scrollTo(0, 0);
                    }}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${active 
                        ? 'bg-[#E8EEFF] text-[#3B5BDB]' 
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-[#3B5BDB] text-white rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Right: Desktop Actions */}
            <div className="hidden md:flex items-center gap-2">
              <NotificationBell />
              
              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                    ${profileMenuOpen 
                      ? 'bg-slate-100 text-slate-900' 
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }
                  `}
                >
                  <div className="w-8 h-8 bg-[#3B5BDB] rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  {userName && (
                    <span className="text-sm font-medium max-w-[120px] truncate">{userName}</span>
                  )}
                  <ChevronDown className={`h-4 w-4 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {profileMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10"
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-20">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-sm font-medium text-slate-900">{userName || 'User'}</p>
                        {policyNumber && (
                          <p className="text-xs text-slate-500 font-mono mt-0.5">{policyNumber}</p>
                        )}
                      </div>
                      
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate('/settings?tab=profile');
                            setProfileMenuOpen(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                        >
                          <User className="h-4 w-4 text-slate-400" />
                          My Profile
                        </button>
                        <button
                          onClick={() => {
                            navigate('/settings');
                            setProfileMenuOpen(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                        >
                          <Settings className="h-4 w-4 text-slate-400" />
                          Settings
                        </button>
                      </div>
                      
                      <div className="border-t border-slate-100 pt-1">
                        <button
                          onClick={() => {
                            handleLogout();
                            setProfileMenuOpen(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                        >
                          <LogOut className="h-4 w-4" />
                          Log Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right: Mobile Nav */}
            <div className="md:hidden flex items-center gap-2">
              <NotificationBell />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          {/* Mobile Page Title */}
          <div className="md:hidden pb-3 -mt-1">
            <span className="text-sm font-medium text-slate-600">{pageTitle}</span>
          </div>
        </div>

        {/* Mobile Navigation Menu - Rendered via Portal to avoid stacking context issues */}
      </header>

      {/* Mobile Navigation - Portal to dedicated container for proper z-index stacking */}
      {portalContainer && mobileMenuOpen && createPortal(
        <PortalNavigation
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          onLogout={handleLogout}
          policyNumber={policyNumber}
          userName={userName}
          claimsCount={claimsCount}
        />,
        portalContainer
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <PortalFooter />
    </div>
  );
}
